/**
 * TanStack Query hooks for the admin product-review queue — PIM Wave P3
 * Task 3. Mirrors the P1 `attribute-fields/hooks.ts` idiom (own api.ts,
 * own hooks.ts, prefix-invalidate helper) and the seller-applications
 * queue behavior (30s refetch, 10s stale, oldest-first, limit 50,
 * `staleTime: 0` on the detail-on-open query).
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { AdminProduct } from "@/features/admin/products/api";
import {
  approveProduct,
  getProductReview,
  listReviewProducts,
  productReviewKeys,
  sendBackProduct,
  type ProductReviewResponse,
  type ReviewListResponse,
} from "@/features/admin/product-review/api";

/** Mirrors `queue.list` shape (`useSellerApplications.ts:39`). */
const REFETCH_INTERVAL = 30_000;
const PAGE_LIMIT = 50;

export function useReviewProducts(
  status: string,
): UseInfiniteQueryResult<InfiniteData<ReviewListResponse>, Error> {
  return useInfiniteQuery<
    ReviewListResponse,
    Error,
    InfiniteData<ReviewListResponse>,
    ReturnType<typeof productReviewKeys.list>,
    string | null
  >({
    queryKey: productReviewKeys.list(status),
    queryFn: ({ pageParam }) =>
      listReviewProducts({ status, cursor: pageParam, limit: PAGE_LIMIT }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? null,
    refetchInterval: REFETCH_INTERVAL,
    staleTime: 10_000,
  });
}

export function useProductReview(
  id: string | null,
): UseQueryResult<ProductReviewResponse, Error> {
  return useQuery<ProductReviewResponse, Error>({
    queryKey: productReviewKeys.detail(id ?? ""),
    queryFn: () => getProductReview(id!),
    enabled: id !== null,
    // Always re-fetch on drawer open — the review checklist must reflect
    // the latest attributes/completeness, not a stale cached read.
    staleTime: 0,
  });
}

function useInvalidateAfterReviewWrite(): (id?: string) => Promise<void> {
  const qc = useQueryClient();
  return async (id?: string) => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["admin", "product-review", "list"] }),
      ...(id
        ? [qc.invalidateQueries({ queryKey: productReviewKeys.detail(id) })]
        : []),
    ]);
  };
}

export function useApproveProduct(): UseMutationResult<
  AdminProduct,
  Error,
  string
> {
  const invalidate = useInvalidateAfterReviewWrite();
  return useMutation<AdminProduct, Error, string>({
    mutationFn: (id) => approveProduct(id),
    onSuccess: (_data, id) => {
      void invalidate(id);
    },
  });
}

export interface SendBackVariables {
  id: string;
  reason: string;
}

export function useSendBackProduct(): UseMutationResult<
  AdminProduct,
  Error,
  SendBackVariables
> {
  const invalidate = useInvalidateAfterReviewWrite();
  return useMutation<AdminProduct, Error, SendBackVariables>({
    mutationFn: ({ id, reason }) => sendBackProduct(id, reason),
    onSuccess: (_data, { id }) => {
      void invalidate(id);
    },
  });
}
